"use client";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getCookie, setCookie, setLocal, getLocal, checkDevice } from "@/Snnipets/helper";
import { gclidData, gclidDate, countryData, UserIPStore, UserIPv6, referralLink, landingPage, indexurl, gclidName } from "@/lib/variables";

const TrackingContext = createContext(null);

/* ===============================
   ✅ Utility helpers
================================ */
const isEmpty = (v) => v == null || v === "undefined" || v === "null" || (typeof v === "string" && v.trim() === "");

const safeLower = (v) => (typeof v === "string" ? v.toLowerCase() : null);

/* ===============================
   ✅ Abortable fetch
================================ */
async function fetchWithTimeout(url, timeout = 5000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(id));
}

export function TrackingProvider({ children }) {
  const [isTrackingReady, setTrackingReady] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const initializedRef = useRef(false);

  /* ===============================
     ✅ IP
  ================================ */
  async function getIPv4() {
    try {
      const res = await fetchWithTimeout("https://api.ipify.org?format=json");
      const data = await res.json();
      if (data?.ip) {
        setCookie(UserIPStore, data.ip.trim(), 8);
        return data.ip.trim();
      }
    } catch {}
    return null;
  }

  async function getIPv6() {
    try {
      const res = await fetchWithTimeout("https://api6.ipify.org?format=json");
      const data = await res.json();
      if (data?.ip) {
        setCookie(UserIPv6, data.ip.trim(), 8);
        return data.ip.trim();
      }
    } catch {}
    return null;
  }

  async function initUserIPs() {
    let ipv4 = getCookie(UserIPStore);
    let ipv6 = getCookie(UserIPv6);

    if (isEmpty(ipv4)) ipv4 = await getIPv4();
    if (isEmpty(ipv6)) await getIPv6();

    return ipv4;
  }

  /* ===============================
     ✅ Logging
  ================================ */
  async function sendLeadLog() {
    if (typeof window === "undefined") return;

    const payload = {
      dateandtime: new Date().toLocaleString("en-CA", { timeZone: "Asia/Kolkata" }).replace("T", " "),
      logid: "visitor",
      UserIP: getCookie(UserIPStore) || "",
      UserIPv6: getCookie(UserIPv6) || "",
      landingPage: getCookie(landingPage) || "",
      referralLink: getCookie(referralLink) || "",
      countryData: getCookie(countryData),
      gclidData: getLocal(gclidData),
      gclidDate: getLocal(gclidDate),
      currenturl: getLocal(indexurl),
      accessDevices: checkDevice(),
    };

    try {
      await fetch(`${process.env.NEXT_PUBLIC_FAILPROCCESSAPI}/failprocess/logs/log_create.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } catch (err) {
      console.error("Lead log failed:", err);
    }
  }

  /* ===============================
     ✅ Country detection
  ================================ */
  async function detectCountry() {
    try {
      const res = await fetchWithTimeout("https://ipapi.co/json");
      const data = await res.json();
      return safeLower(data?.country_code);
    } catch {
      return null;
    }
  }

  async function detectCountryFallback(ip) {
    if (!ip) return null;
    try {
      const res = await fetchWithTimeout(`https://free.freeipapi.com/api/json/${encodeURIComponent(ip)}`);
      const data = await res.json();
      return safeLower(data?.countryCode);
    } catch {
      return null;
    }
  }

  async function setCountrySafely() {
    try {
      const existing = getCookie(countryData);
      if (!isEmpty(existing)) {
        try {
          setCountryCode(JSON.parse(existing));
          return;
        } catch {}
      }

      let country = await detectCountry();
      if (!country) {
        const ip = getCookie(UserIPStore);
        country = await detectCountryFallback(ip);
      }

      country = country || "us";
      setCookie(countryData, JSON.stringify(country), 8);
      setCountryCode(country);
    } catch {
      setCookie(countryData, JSON.stringify("us"), 8);
      setCountryCode("us");
    }
  }

  /* ===============================
   ✅ MAIN INIT (LAST VISIT)
================================ */
  useEffect(() => {
    if (initializedRef.current || typeof window === "undefined") return;
    initializedRef.current = true;

    (async () => {
      try {
        const userIP = await initUserIPs();

        const referralCookie = getCookie(referralLink);
        const landingCookie = getCookie(landingPage);
        const referrer = document.referrer;

        let newReferral = "";
        let referralSource = ""; // 👉 track source

        const blockedIPs = ["103.244.122.110", "122.170.7.58", "182.156.109.238"];
        // const blockedIPs = ["104.244.122.110"];

        /* ===============================
         🔹 FIND NEW REFERRAL
      ================================ */

        // 1️⃣ document.referrer (EXTERNAL ONLY)
        if (!isEmpty(referrer)) {
          try {
            const referrerHost = new URL(referrer).host;
            const currentHost = window.location.host;

            if (referrerHost !== currentHost) {
              newReferral = referrer;
              referralSource = "referrer";
            }
          } catch {}
        }

        // 2️⃣ utm_source from CURRENT URL
        if (isEmpty(newReferral)) {
          const params = new URLSearchParams(window.location.search);
          const utmSource = params.get("utm_source");

          if (!isEmpty(utmSource)) {
            newReferral = utmSource;
            referralSource = "url_utm";
          }
        }

        // 3️⃣ utm_source from STORED LANDING PAGE COOKIE (FALLBACK)
        if (isEmpty(newReferral) && !isEmpty(landingCookie)) {
          try {
            const landingURL = new URL(landingCookie);
            const utmSource = new URLSearchParams(landingURL.search).get("utm_source");

            if (!isEmpty(utmSource)) {
              newReferral = utmSource;
              referralSource = "landing_cookie";
            }
          } catch {}
        }

        /* ===============================
         🔹 UPDATE COOKIES (LAST VISIT)
      ================================ */
        const hasNewReferral = !isEmpty(newReferral) && newReferral !== referralCookie;

        if (hasNewReferral) {
          // ✅ Always update referral
          setCookie(referralLink, newReferral, 8640);

          // ✅ Update landing ONLY if referral is NOT from landing cookie
          if (referralSource !== "landing_cookie") {
            setCookie(landingPage, window.location.href, 8640);
            if (!blockedIPs.includes(userIP)) {
              if (isEmpty(getCookie(countryData))) {
                await setCountrySafely();
              }
              await sendLeadLog();
            }
          }
        } else {
          // ✅ Ensure landing page exists
          if (isEmpty(landingCookie)) {
            setCookie(landingPage, window.location.href, 8640);
            if (!blockedIPs.includes(userIP)) {
              if (isEmpty(getCookie(countryData))) {
                await setCountrySafely();
              }
              await sendLeadLog();
            }
          }
        }

        /* ===============================
         🔹 LOGGING
      ================================ */

        await setCountrySafely();
        setTrackingReady(true);
      } catch (err) {
        console.error("Tracking init failed:", err);
      }
    })();
  }, []);

  /* ===============================
     ✅ GCLID
  ================================ */
  useEffect(() => {
    if (typeof window === "undefined") return;

    const getParamFromLanding = (param) => {
      try {
        const landing = getCookie(landingPage);
        if (!landing) return null;

        return new URL(landing).searchParams.get(param);
      } catch (err) {
        console.warn("Invalid Landing URL");
        return null;
      }
    };

    const params = new URLSearchParams(window.location.search);

    let _gclid = params.get("gclid") || getParamFromLanding("gclid");
    let _gclidName = params.get("gcl_campaign") || getParamFromLanding("gcl_campaign");

    if (!isEmpty(_gclid)) {
      const storedGclid = getLocal(gclidData);
      if (isEmpty(storedGclid)) {
        setLocal(gclidData, _gclid);
        setLocal(gclidDate, Date.now().toString());
      }
    }
    // ✅ Store campaign name only once
    if (!isEmpty(_gclidName)) {
      const storedName = getLocal(gclidName);
      if (isEmpty(storedName)) {
        setLocal(gclidName, _gclidName);
      }
    }

    setLocal(indexurl, window.location.href);
  }, []);

  return <TrackingContext.Provider value={{ isTrackingReady, setTrackingReady, countryCode }}>{children}</TrackingContext.Provider>;
}

export function useTrackingContext() {
  return useContext(TrackingContext);
}
